import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Mail, ImageOff } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { teams } from '../teamData';
import { MatchKitPreview } from '../components/MatchKitPreview';
import { ClubCampaignBanner } from '../components/ClubCampaignBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamPage() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const team = teams[slug];

  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  if (!team) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex flex-col items-center justify-center text-center px-6" data-testid="team-not-found">
        <h1 className="font-bebas text-5xl text-white">404</h1>
        <p className="font-dm text-white/60 mt-2">{lang === 'fr' ? 'Club introuvable.' : 'Club not found.'}</p>
        <Link to="/clubs" className="mt-6 text-[#CE2B37] font-dm text-sm uppercase tracking-wider">← {lang === 'fr' ? 'Tous les clubs' : 'All clubs'}</Link>
      </div>
    );
  }

  const tc = lang === 'fr' ? team.fr : team.en;
  const L = lang === 'fr'
    ? { download: 'Télécharger le PDF', emailIt: 'Envoyer', placeholder: 'Adresse courriel', soon: 'Photo à venir', sending: 'Envoi...', palette: 'PALETTE', colorway: 'COLORIS DU CLUB', pieces: 'PIÈCES', size: 'TAILLES', items: 'pièces', crest: 'ÉCUSSON : BRODÉ', fabric: 'TISSU', rev: 'RÉVISION 001', supplier: 'FOURNISSEUR OFFICIEL', getKit: 'Obtenir cette collection', est: 'Fondé en' }
    : { download: 'Download PDF', emailIt: 'Send', placeholder: 'Recipient email address', soon: 'Photo coming soon', sending: 'Sending...', palette: 'COLOR PALETTE', colorway: 'CLUB COLORWAY', pieces: 'PIECES', size: 'SIZE RANGE', items: 'items', crest: 'CREST: EMBROIDERED', fabric: 'FABRIC', rev: 'REVISION 001', supplier: 'OFFICIAL SUPPLIER', getKit: 'Request this collection', est: 'Est.' };

  const downloadPdf = () => window.open(`${API}/teams/${slug}/pdf?lang=${lang}`, '_blank');

  const kitMessage = lang === 'fr'
    ? `Bonjour, je souhaite obtenir une soumission pour la collection du club ${team.name} (${team.items.length} pièces). Merci de m'envoyer les prix et les options de personnalisation.`
    : `Hello, I'd like to request a quote for the ${team.name} club collection (${team.items.length} pieces). Please send pricing and customization options.`;
  const kitHref = `/contact?org=${encodeURIComponent(team.name)}&message=${encodeURIComponent(kitMessage)}`;

  const emailPdf = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/teams/${slug}/email-pdf?lang=${lang}`, { email });
      if (data.sent) {
        toast.success(lang === 'fr' ? `PDF envoyé à ${email}` : `PDF sent to ${email}`);
        setEmail('');
      } else {
        toast.error(lang === 'fr' ? 'Service courriel non configuré pour le moment.' : 'Email service not configured yet.');
      }
    } catch (err) {
      toast.error(lang === 'fr' ? "Échec de l'envoi." : 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="font-dm bg-[#EDEBE6] text-[#14213D]" data-testid="team-page">
      {/* Hero banner (dark) */}
      {team.bannerPresentation === 'campaign' ? (
        <ClubCampaignBanner team={team} lang={lang} />
      ) : (
      <section className="relative overflow-hidden border-b border-[#14213D]" data-testid="team-hero">
        <div className="relative h-[52vh] min-h-[400px]">
          {team.banner ? (
            <img
              src={team.banner}
              alt={team.name}
              className="absolute inset-0 w-full h-full object-cover object-[center_28%]"
            />
          ) : (
            <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 30% 20%, ${team.sky} 0%, ${team.navy} 55%, #0B1428 100%)` }}>
              <img src={team.logo} alt="" aria-hidden className="absolute right-6 md:right-16 top-1/2 -translate-y-1/2 h-[85%] w-auto object-contain opacity-15" />
            </div>
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,33,61,0.55) 0%, rgba(20,33,61,0.15) 38%, rgba(20,33,61,0.85) 100%)' }} />

          {/* Spec strip */}
          <div className="absolute top-0 left-0 right-0 pt-24 md:pt-28 px-6 md:px-10">
            <div className="flex items-center justify-between gap-4 border-b border-white/20 pb-4 flex-wrap">
              <div className="flex items-baseline gap-3">
                <span className="font-bebas text-2xl text-white tracking-tight">mass</span>
                <span className="font-dm text-[10px] md:text-[11px] tracking-[2px] text-white/60">× DM SPORTS</span>
              </div>
              <div className="hidden md:block font-dm text-[11px] tracking-[3px] font-semibold text-white/90 text-center">
                {team.name.toUpperCase()} — {lang === 'fr' ? 'COLLECTION CLUB' : 'CLUB COLLECTION'}
              </div>
              <div className="font-dm text-[10px] md:text-[11px] tracking-[2px] text-white/60">
                {lang === 'fr' ? 'SAISON' : 'SEASON'}: <span className="text-white font-semibold">{team.season}</span>
              </div>
            </div>
          </div>

          {/* Hero bottom content */}
          <div className="absolute left-0 right-0 bottom-0 px-6 md:px-10 pb-8 md:pb-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center justify-between gap-6 flex-wrap mb-2 md:mb-3">
                <div className="flex items-center gap-3">
                  <img src={team.logo} alt={team.name} className="w-12 h-12 md:w-14 md:h-14 object-contain drop-shadow-lg" data-testid="team-crest" />
                  <p className="font-dm text-[11px] md:text-xs tracking-[3px] text-[#7FD0F5]">{tc.collectionTitle}{team.founded ? ` · ${L.est} ${team.founded}` : ''}</p>
                </div>
                <div className="font-dm text-[10px] md:text-xs leading-[1.8] tracking-[1px] text-white/75 text-right">
                  {tc.categories}<br />{tc.suppliedBy}
                </div>
              </div>
              <h1 className="font-bebas text-white leading-[0.82] tracking-tight w-full" style={{ fontSize: 'clamp(60px, 13vw, 172px)' }} data-testid="team-collection-title">
                {team.name}
              </h1>
            </motion.div>
          </div>
        </div>
      </section>
      )}

      {/* Palette strip */}
      <section className="grid grid-cols-2 lg:grid-cols-4 border-b border-[#14213D]" data-testid="team-spec-strip">
        <div className="p-6 md:p-7 border-r border-[#C9C6BE] border-b lg:border-b-0">
          <div className="font-dm text-[10px] tracking-[2px] text-[#6B6F7B] mb-3">{L.palette}</div>
          <div className="flex gap-2.5">
            {team.palette.map((c) => (
              <div key={c} className="w-9 h-9 border border-[#14213D]" style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
        <div className="p-6 md:p-7 lg:border-r border-[#C9C6BE] border-b lg:border-b-0">
          <div className="font-dm text-[10px] tracking-[2px] text-[#6B6F7B] mb-3">{L.colorway}</div>
          <div className="text-base md:text-lg font-medium">{lang === 'fr' ? team.colorwayFr : team.colorwayEn}</div>
        </div>
        <div className="p-6 md:p-7 border-r border-[#C9C6BE]">
          <div className="font-dm text-[10px] tracking-[2px] text-[#6B6F7B] mb-3">{L.pieces}</div>
          <div className="text-base md:text-lg font-medium" data-testid="team-piece-count">{team.items.length} {L.items}</div>
        </div>
        <div className="p-6 md:p-7">
          <div className="font-dm text-[10px] tracking-[2px] text-[#6B6F7B] mb-3">{L.size}</div>
          <div className="text-base md:text-lg font-medium">{lang === 'fr' ? team.sizeRangeFr : team.sizeRangeEn}</div>
        </div>
      </section>

      {/* Actions bar */}
      <section className="flex flex-wrap items-center gap-4 px-6 md:px-10 py-6 border-b border-[#14213D]" data-testid="team-actions">
        <Link
          to={kitHref}
          className="inline-flex items-center gap-2 bg-[#CE2B37] text-white font-dm text-[11px] tracking-[2px] uppercase px-6 py-3.5 hover:bg-[#a82230] transition-colors duration-200"
          data-testid="team-request-kit"
        >
          <Mail size={16} /> {L.getKit}
        </Link>
        <button
          onClick={downloadPdf}
          className="inline-flex items-center gap-2 bg-[#14213D] text-white font-dm text-[11px] tracking-[2px] uppercase px-6 py-3.5 hover:bg-[#2E9BD6] transition-colors duration-200"
          data-testid="team-download-pdf"
        >
          <Download size={16} /> {L.download}
        </button>
        <form onSubmit={emailPdf} className="flex items-stretch gap-0 flex-1 basis-[280px] min-w-0 max-w-md" data-testid="team-email-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={L.placeholder}
            required
            className="flex-1 min-w-0 bg-[#F6F5F1] border border-[#14213D] border-r-0 text-[#14213D] placeholder:text-[#6B6F7B] font-dm text-xs px-4 py-3.5 focus:outline-none focus:bg-white transition-colors"
            data-testid="team-email-input"
          />
          <button
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-2 bg-[#2E9BD6] text-white font-dm text-[11px] tracking-[2px] uppercase px-5 py-3.5 hover:bg-[#14213D] transition-colors duration-200 disabled:opacity-50 whitespace-nowrap"
            data-testid="team-email-pdf"
          >
            <Mail size={16} /> {sending ? L.sending : L.emailIt}
          </button>
        </form>
      </section>

      {/* Intro */}
      <section className="px-6 md:px-10 pt-12 md:pt-14">
        <p className="max-w-3xl text-base md:text-lg leading-relaxed text-[#4A4F5E]" data-testid="team-intro">{tc.intro}</p>
      </section>

      <MatchKitPreview kit={team.matchKit} lang={lang} />

      {/* Product grid */}
      <section className="px-6 md:px-10 pt-10 pb-6" data-testid="team-kit-section">
        <div className="flex items-baseline justify-between mb-7 flex-wrap gap-3">
          <h2 className="font-bebas text-4xl md:text-5xl tracking-tight uppercase">{tc.lineup}</h2>
          <div className="font-dm text-[11px] tracking-[2px] text-[#6B6F7B]">{L.rev} · 2027</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {team.items.map((item, i) => {
            const name = lang === 'fr' ? item.nameFr : item.name;
            const tag = lang === 'fr' ? item.tagFr : item.tag;
            const colorway = lang === 'fr' ? item.colorFr : item.color;
            const desc = lang === 'fr' ? item.descFr : item.desc;
            const fabric = lang === 'fr' ? item.fabricFr : item.fabric;
            return (
              <motion.article
                key={item.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.07 }}
                className="bg-[#F6F5F1] border border-[#14213D] flex flex-col group"
                data-testid={`team-item-${item.n}`}
              >
                {/* Card header */}
                <div className="flex justify-between items-center px-4 md:px-5 py-3 border-b border-[#C9C6BE]">
                  <span className="font-dm text-[11px] tracking-[2px] font-semibold" data-testid={`team-item-${item.n}-tag`}>{tag}</span>
                  <span className="font-dm text-[11px] text-[#6B6F7B]" data-testid={`team-item-${item.n}-code`}>{item.code}</span>
                </div>

                {/* Image */}
                <div className="aspect-square flex items-center justify-center p-1.5 bg-[#FBFAF7] overflow-hidden">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={name}
                      data-testid={`team-item-${item.n}-image`}
                      className="w-full h-full object-contain scale-[1.05] transition-transform duration-500 group-hover:scale-[1.11]"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-[#B4B4B4]" data-testid={`team-item-${item.n}-placeholder`}>
                      <ImageOff size={34} />
                      <span className="font-dm text-xs mt-2">{L.soon}</span>
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div className="p-5 border-t border-[#C9C6BE] flex-1 flex flex-col gap-3">
                  <div className="flex flex-wrap justify-between items-baseline gap-x-3 gap-y-2">
                    <h3 className="font-bebas text-2xl tracking-wide leading-none" data-testid={`team-item-${item.n}-name`}>{name}</h3>
                    <span className="font-dm text-[11px] tracking-[1px] text-[#2E9BD6] font-semibold break-words" data-testid={`team-item-${item.n}-color`}>{colorway.toUpperCase()}</span>
                  </div>
                  <p className="text-sm leading-relaxed text-[#4A4F5E]" data-testid={`team-item-${item.n}-description`}>{desc}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-auto pt-3 border-t border-dashed border-[#C9C6BE] font-dm text-[10.5px] tracking-[1px] text-[#6B6F7B]">
                    <span data-testid={`team-item-${item.n}-fabric`}>{L.fabric}: {fabric}</span>
                    {item.crest && <span data-testid={`team-item-${item.n}-crest`}>{L.crest}</span>}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* Footer bar */}
      <footer className="border-t border-[#14213D] px-6 md:px-10 py-6 flex justify-between items-center gap-6 flex-wrap" data-testid="team-footer-bar">
        <div className="flex items-baseline gap-3">
          <span className="font-bebas text-lg">mass</span>
          <span className="font-dm text-[10px] tracking-[2px] text-[#6B6F7B]">× DM SPORTS · {L.supplier}</span>
        </div>
        <div className="font-dm text-[10px] tracking-[2px] text-[#6B6F7B]">{team.name.toUpperCase()}{team.founded ? ` · EST. ${team.founded}` : ''} · {team.city.toUpperCase()}</div>
      </footer>
    </div>
  );
}
